/**
 * KeywordChips — Compact interactive keyword suggestion card.
 *
 * Shows AI-suggested keywords as selectable checkboxes, current keywords,
 * a compact custom input, and a right-aligned "Apply" button.
 * Designed for responsive grid layout.
 *
 * Features:
 * - Skeleton loading when AI is still generating keywords
 * - Current keywords displayed visibly in the card
 * - Checkbox selection for suggestions + custom input
 */

import { useState, useMemo, useRef, useEffect } from 'react'
import type { KeywordChipsBlock } from '../../../stores/CoPilotStore'

const API_BASE = import.meta.env.VITE_API_BASE || ''

type Status = 'idle' | 'loading' | 'success' | 'error'

export function KeywordChips({ block }: { block: KeywordChipsBlock }) {
  const { asin, sku, title, currentKeywords, suggestedKeywords } = block

  // Track whether the block has been updated at least once (enrichment attempted)
  const prevBlockRef = useRef(block)
  const [enrichmentDone, setEnrichmentDone] = useState(false)

  useEffect(() => {
    if (block !== prevBlockRef.current) {
      prevBlockRef.current = block
      setEnrichmentDone(true)
    }
  }, [block])

  // Show skeleton only while AI is still generating AND enrichment hasn't been attempted yet
  const isGenerating = suggestedKeywords.length === 0 && !enrichmentDone

  // All suggestions start selected
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(suggestedKeywords.map((_, i) => i))
  )
  const [customInput, setCustomInput] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const [showCurrentKeywords, setShowCurrentKeywords] = useState(false)

  // Re-sync selected set when suggestedKeywords changes (AI enrichment arrives)
  const prevCount = selected.size
  if (suggestedKeywords.length > 0 && prevCount === 0 && status === 'idle') {
    const newSet = new Set(suggestedKeywords.map((_, i) => i))
    if (newSet.size > 0) setSelected(newSet)
  }

  // Parse custom keywords from comma-separated input
  const customKeywords = useMemo(
    () =>
      customInput
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k.length > 0),
    [customInput]
  )

  // Count of keywords to apply
  const selectedSuggestions = suggestedKeywords.filter((_, i) => selected.has(i))
  const totalToApply = selectedSuggestions.length + customKeywords.length

  function toggleChip(idx: number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) {
        next.delete(idx)
      } else {
        next.add(idx)
      }
      return next
    })
  }

  async function handleApply() {
    if (totalToApply === 0) return
    setStatus('loading')
    setErrorMsg('')

    // Merge: existing keywords + selected suggestions + custom
    const existing = currentKeywords ? currentKeywords.trim() : ''
    const newKeywords = [...selectedSuggestions, ...customKeywords].join(', ')
    const merged = existing ? `${existing}, ${newKeywords}` : newKeywords

    try {
      const res = await fetch(
        `${API_BASE}/amazon/listings/${encodeURIComponent(sku)}/keywords`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ keywords: merged }),
        }
      )

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error((data as any).error || `Failed (${res.status})`)
      }

      setStatus('success')
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong')
      setStatus('error')
    }
  }

  function handleRetry() {
    setStatus('idle')
    setErrorMsg('')
  }

  return (
    <div className="border border-[#E4E4E7] rounded-xl bg-white overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-[#F4F4F5] flex items-center gap-2">
        <KeywordIcon />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-[#18181B] truncate">
            {title}
          </div>
        </div>
        <span className="text-[10px] font-mono text-[#A1A1AA] bg-[#F4F4F5] px-1.5 py-0.5 rounded shrink-0">
          {asin}
        </span>
      </div>

      <div className="px-3 py-2.5 space-y-2 flex-1 flex flex-col">
        {/* Current keywords */}
        {currentKeywords && (
          <div className="space-y-1">
            <button
              onClick={() => setShowCurrentKeywords((v) => !v)}
              className="flex items-center gap-1 text-[11px] text-[#71717A] hover:text-[#52525B] cursor-pointer transition-colors"
            >
              <svg
                className={`size-3 transition-transform ${showCurrentKeywords ? 'rotate-90' : ''}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
              </svg>
              Current keywords
            </button>
            {showCurrentKeywords && (
              <div className="text-[11px] text-[#A1A1AA] leading-snug pl-4 pb-1">
                {currentKeywords}
              </div>
            )}
          </div>
        )}

        {/* Skeleton loading state */}
        {isGenerating && (
          <div className="space-y-1.5 animate-pulse">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="flex items-center gap-2 py-1 px-1.5">
                <div className="size-3.5 rounded bg-[#E4E4E7]" />
                <div className="h-3 bg-[#F4F4F5] rounded" style={{ width: `${50 + n * 12}%` }} />
              </div>
            ))}
            <div className="h-8 w-full bg-[#F4F4F5] rounded-lg mt-1" />
            <div className="flex justify-end mt-1">
              <div className="h-7 w-20 bg-[#F4F4F5] rounded-lg" />
            </div>
          </div>
        )}

        {/* Suggested keywords as checkbox list */}
        {!isGenerating && suggestedKeywords.length > 0 && status !== 'success' && (
          <div className="space-y-0.5">
            {suggestedKeywords.map((kw, i) => {
              const isSelected = selected.has(i)
              return (
                <label
                  key={i}
                  className={`
                    flex items-center gap-2 py-1 px-1.5 -mx-1.5 rounded-md cursor-pointer
                    transition-colors hover:bg-[#F4F4F5]
                    ${status === 'loading' ? 'opacity-50 pointer-events-none' : ''}
                  `}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleChip(i)}
                    disabled={status === 'loading'}
                    className="size-3.5 rounded border-[#D4D4D8] text-[#3B82F6] accent-[#3B82F6] cursor-pointer"
                  />
                  <span className="text-[12px] text-[#3F3F46] leading-snug">{kw}</span>
                </label>
              )
            })}
          </div>
        )}

        {/* Custom keywords input */}
        {!isGenerating && status !== 'success' && (
          <input
            type="text"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            placeholder="Add custom keywords..."
            disabled={status === 'loading'}
            className="w-full text-[12px] px-2.5 py-1.5 rounded-lg border border-[#D4D4D8] bg-white placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/20 focus:border-[#3B82F6] disabled:opacity-50"
          />
        )}

        {/* Spacer to push action area to bottom */}
        <div className="flex-1" />

        {/* Action area */}
        {!isGenerating && status === 'idle' && (
          <div className="flex justify-end">
            <button
              onClick={handleApply}
              disabled={totalToApply === 0}
              className={`
                py-1.5 px-3 rounded-lg text-[12px] font-semibold transition-colors
                ${
                  totalToApply > 0
                    ? 'bg-[#3B82F6] text-white hover:bg-[#2563EB] cursor-pointer'
                    : 'bg-[#F4F4F5] text-[#A1A1AA] cursor-not-allowed'
                }
              `}
            >
              {totalToApply > 0
                ? `Apply ${totalToApply}`
                : 'Select keywords'}
            </button>
          </div>
        )}

        {status === 'loading' && (
          <div className="flex items-center justify-center gap-1.5 py-1.5 text-[12px] text-[#3B82F6]">
            <LoadingSpinner />
            Updating...
          </div>
        )}

        {status === 'success' && (
          <div className="flex items-center gap-1.5 py-1.5 px-2.5 rounded-lg bg-[#F0FDF4] border border-[#BBF7D0] text-[12px] text-[#166534]">
            <svg className="size-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
            </svg>
            Submitted
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 py-1.5 px-2.5 rounded-lg bg-[#FEF2F2] border border-[#FECACA] text-[11px] text-[#991B1B]">
              <svg className="size-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
              {errorMsg || 'Failed'}
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleRetry}
                className="py-1 px-2.5 rounded-lg text-[11px] font-semibold bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7] cursor-pointer transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function KeywordIcon() {
  return (
    <svg
      className="size-4 text-[#3B82F6]"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  )
}

function LoadingSpinner() {
  return (
    <svg className="size-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}
